/*
 * Copyright (c) 2020 Little Robot Friends, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import React from 'react'
import {NavItemTitle} from '../components/app/sidebar'

export const copyright = {
  name: 'Little Robot Friends Inc.',
  url: 'https://littlerobotfriends.com',
}

export const routes = {
  roleSelector: {
    name: 'Select Role',
    url: '/',
  },
  onboarding: {
    name: 'Onboarding',
    url: '/onboarding',
  },
  home: {
    name: 'Home',
    url: '/home',
  },
  game: {
    name: 'Game',
    url: '/game',
  },
  play: {
    name: 'Play',
    url: '/play',
  },
  studentProgress: {
    name: 'Student progress',
    url: '/student-progress',
  },
  login: {
    name: 'Log in',
    url: '/auth/login',
  },
  passwordReset: {
    name: 'Password reset',
    url: '/auth/password-reset',
  },
  register: {
    name: 'Register',
    url: '/auth/register',
  },
  profile: {
    name: 'Profile',
    url: '/profile',
  },
  settings: {
    name: 'Settings',
    url: '/settings',
  },
  enrollments: {
    name: 'Enrollments',
    url: '/enrollments',
  },
  credits: {
    name: 'Credits',
    url: '/credits',
  },
  objectives: {
    name: 'Objectives',
    url: '/#objectives',
  },
  achievements: {
    name: 'Achievements',
    url: '/#achievements',
  },
  troubleshooting: {
    name: 'Troubleshooting',
    url: '/troubleshooting',
  },
  userFeedback: {
    name: 'User feedback',
    url: '/#user-feedback',
  },
  action: {
    name: 'Actions',
    url: '/__auth/action',
  },
  signUp: {
    name: 'Sign Up',
    url: '/signup',
  },
}

export const navigation = {
  xl: {
    sidebar: [
      {
        subNav: [
          {
            title: <NavItemTitle icon="home" title={routes.home.name} />,
            itemId: routes.home.url,
          },
        ],
      },
      {
        title: 'Students',
        subNav: [
          {
            title: <NavItemTitle icon="dice-six" title={routes.game.name} />,
            itemId: routes.game.url,
          },
          {
            title: <NavItemTitle icon="tachometer-alt" title={routes.studentProgress.name} />,
            itemId: routes.studentProgress.url,
          },
          {
            title: <NavItemTitle icon="lock" title={routes.objectives.name} disabled />,
            itemId: routes.objectives.url,
          },
          {
            title: <NavItemTitle icon="lock" title={routes.achievements.name} disabled />,
            itemId: routes.achievements.url,
          },
        ],
      },
      {
        title: 'Help',
        subNav: [
          {
            title: <NavItemTitle icon="fist-raised" title={routes.troubleshooting.name} />,
            itemId: routes.troubleshooting.url,
          },
          {
            title: <NavItemTitle icon="lock" title={routes.userFeedback.name} disabled />,
            itemId: routes.userFeedback.url,
          },
        ],
      },
    ],
  },
  large: {
    auth: [
      {
        title: 'Choose Player',
        icon: () => <FontAwesomeIcon icon="gamepad" />,
        url: routes.roleSelector.url,
      },
      {
        title: 'Profile',
        icon: () => <FontAwesomeIcon icon="user-circle" />,
        url: routes.profile.url,
      },
      {
        title: 'Enrollments',
        icon: () => <FontAwesomeIcon icon="receipt" />,
        url: routes.enrollments.url,
      },
      {
        title: 'Credits',
        icon: () => <FontAwesomeIcon icon="film" />,
        url: routes.credits.url,
      },
      {
        title: 'Log out',
        icon: () => <FontAwesomeIcon icon="sign-out-alt" />,
        url: null,
      },
    ],
  },
  small: {
    auth: [
      {url: routes.roleSelector.url, name: routes.play.name, icon: 'dice-six'},
      {url: routes.profile.url, name: routes.profile.name, icon: 'user-circle'},
      {url: routes.enrollments.url, name: routes.enrollments.name, icon: 'receipt'},
    ],
    footer: [
      {
        title: routes.studentProgress.name,
        icon: 'tachometer-alt',
        url: routes.studentProgress.url,
      },
      {
        title: routes.objectives.name,
        icon: 'lock',
        url: routes.objectives.url,
      },
      {
        title: routes.achievements.name,
        icon: 'lock',
        url: routes.achievements.url,
      },
      {
        title: routes.troubleshooting.name,
        icon: 'fist-raised',
        url: routes.troubleshooting.url,
      },
      {
        title: routes.userFeedback.name,
        icon: 'lock',
        url: routes.userFeedback.url,
      },
    ],
  },
}

export const assets = {
  bkg: {
    day: {
      landscape: '/assets/images/varvista-repeating.svg',
    },
    night: {
      landscape: '/assets/images/varvista-repeating-dark.svg',
    },
  },
  auth: {
    clouds: '/assets/images/hero-clouds.svg',
    hills: '/assets/images/hero-hills.svg',
    varvista: '/assets/images/varvista-repeating.svg',
    varvistaDark: '/assets/images/varvista-repeating-dark.svg',
    location: '/assets/images/bg-auth.jpg',
  },
  logo: {
    location: '/assets/images/sotsc-logo-h.svg',
    verticalPurple: '/assets/images/sotsc-logo-v-purple.svg',
  },
  logoByLRF: {
    location: '/assets/images/sotsc-logo-h-by-lrf.svg',
  },
  navbarBg: {
    location: '/assets/images/navbar-bg.svg',
  },
  sponsors: {
    cmf: '/assets/images/partner-logo-canada-media-fund.svg',
    can: '/assets/images/partner-logo-canada.svg',
    oc: '/assets/images/partner-logo-ontario-creates.svg',
  },
}

export const colors = {
  black: '#000000',
  white: '#FFFFFF',
  green50: '#F0F9F4',
  green100: '#DAF0E3',
  green200: '#AEDDC2',
  green300: '#73C496',
  green400: '#3AA76D',
  green500: '#368759',
  green600: '#2B6B46',
  green700: '#1C472F',
  yellow50: '#FFFAF0',
  yellow100: '#FFF2D9',
  yellow200: '#FFE3AC',
  yellow300: '#FFCF70',
  yellow400: '#FFC043',
  yellow500: '#BC8B2C',
  yellow600: '#997328',
  yellow700: '#664D1B',
  mono100: '#FFFFFF',
  mono200: '#FFF6ED',
  mono300: '#F7EEE6',
  mono400: '#EBE2D9',
  mono500: '#D5CBC1',
  mono600: '#B7AFA7',
  mono700: '#817569',
  mono800: '#5C544C',
  mono900: '#38332E',
  mono1000: '#2E281F',
}

export const sizing = {
  scale0: '2px',
  scale100: '4px',
  scale200: '6px',
  scale300: '8px',
  scale400: '10px',
  scale500: '12px',
  scale550: '14px',
  scale600: '16px',
  scale650: '18px',
  scale700: '20px',
  scale750: '22px',
  scale800: '24px',
  scale900: '32px',
  scale1000: '40px',
  scale1200: '48px',
  scale1400: '56px',
  scale1600: '64px',
  scale2400: '96px',
  scale3200: '128px',
  scale4800: '192px',
}

export const breakpoints = {
  small: 480,
  medium: 840,
  large: 1024,
  xl: 1280,
}

export const errors = {
  noCustomerData: {
    code: 'auth/no-customer-license',
    message: 'No customer license for current user.',
  },
}

export const defaultProfileData = {
  firstName: {
    key: 'First Name',
    value: '',
  },
  lastName: {
    key: 'Last Name',
    value: '',
  },
  displayName: {
    key: 'Display Name',
    value: 'No Display Name',
  },
  email: {
    key: 'Email',
    value: '',
  },
}

export const defaultFieldValues = {
  emptyField: 'Not Specified',
}

export const errorMessages = [
  {name: 'Order', message: 'Looks like no orders were found.'},
  {name: 'Avatar', message: 'No email associated with your account.'},
]
